import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Cloud, 
  LayoutDashboard, 
  Users, 
  FolderOpen, 
  FileText, 
  Search, 
  LogOut,
  Shield,
  ShieldCheck,
  Eye,
  Menu,
  X,
  Settings,
  BarChart3,
  BookOpen,
  Sparkles,
  DraftingCompass,
  FileCode2,
} from 'lucide-react';

const roleBadge = {
  admin: { label: 'Admin', color: 'bg-red-100 text-red-700', icon: ShieldCheck },
  editor: { label: 'Editor', color: 'bg-amber-100 text-amber-700', icon: Shield },
  reader: { label: 'Reader', color: 'bg-slate-100 text-slate-600', icon: Eye },
};

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, role, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Close sidebar on route change (mobile/tablet)
  useEffect(() => {
    if (window.innerWidth < 1024) onClose();
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, show: true },
    { href: '/customers', label: 'Customers', icon: Users, show: true },
    { href: '/projects', label: 'Projects', icon: FolderOpen, show: true },
    { href: '/documentation', label: 'Documentation', icon: FileText, show: true },
    { href: '/design-builder', label: 'Design Builder', icon: DraftingCompass, show: true, also: '/sow-builder' },
    { href: '/code-docs', label: 'Code Docs', icon: FileCode2, show: true },
    { href: '/search', label: 'Search', icon: Search, show: true },
    { href: '/ai-agent', label: 'AI Agent', icon: Sparkles, show: true },
  ];

  const isActive = (href, also) => [href, also].some(h => h && (location.pathname === h || location.pathname.startsWith(h + '/')));

  const badge = roleBadge[role] || roleBadge.reader;
  const BadgeIcon = badge.icon;

  return (
    <>
      {/* Mobile/tablet backdrop */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={onClose} />
      )}

      <aside className={`
        w-64 bg-white border-r border-slate-200 h-screen fixed left-0 top-0 flex flex-col z-50
        transition-transform duration-200 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo + close on mobile/tablet */}
        <div className="h-14 flex items-center justify-between px-5 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2">
            <Cloud className="h-6 w-6 text-[#2B8FD8]" />
            <span className="text-lg font-bold text-slate-900 font-['Plus_Jakarta_Sans']">CloudFactory</span>
          </Link>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-100 lg:hidden" data-testid="close-nav-sidebar">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {navItems.filter(i => i.show).map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href, item.also);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    active
                      ? 'bg-blue-50 text-[#2B8FD8] border-r-4 border-[#2B8FD8] -mr-3 pr-6'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  <Icon className={`h-5 w-5 ${active ? 'text-[#2B8FD8]' : 'text-slate-400'}`} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Admin section */}
          {isAdmin && (
            <div className="mt-6 px-3">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-2">
                Administration
              </div>
              <Link
                to="/admin"
                data-testid="nav-admin"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive('/admin')
                    ? 'bg-blue-50 text-[#2B8FD8]'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <ShieldCheck className={`h-5 w-5 ${isActive('/admin') ? 'text-[#2B8FD8]' : 'text-slate-400'}`} />
                Admin Panel
              </Link>
              <Link
                to="/analytics"
                data-testid="nav-analytics"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive('/analytics')
                    ? 'bg-blue-50 text-[#2B8FD8]'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <BarChart3 className={`h-5 w-5 ${isActive('/analytics') ? 'text-[#2B8FD8]' : 'text-slate-400'}`} />
                Analytics
              </Link>
            </div>
          )}
        </ScrollArea>

        {/* User section */}
        <div className="border-t border-slate-200 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#2B8FD8] flex items-center justify-center text-white font-semibold text-sm shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name || 'User'}</p>
                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${badge.color}`}>
                  <BadgeIcon className="h-2.5 w-2.5" />
                  {badge.label}
                </span>
              </div>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <div className="space-y-1">
            <Link to="/settings">
              <Button
                variant="ghost"
                className="w-full justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-50 h-9"
                data-testid="nav-settings"
              >
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
            </Link>
            <Link to="/userguide">
              <Button
                variant="ghost"
                className="w-full justify-start text-slate-600 hover:text-[#2B8FD8] hover:bg-blue-50 h-9"
                data-testid="nav-userguide"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                User Guide
              </Button>
            </Link>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50 h-9"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

const Layout = ({ children }) => {
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar isOpen={navOpen} onClose={() => setNavOpen(false)} />

      {/* Mobile/tablet top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-12 bg-white border-b border-slate-200 z-30 flex items-center px-3 gap-2">
        <button onClick={() => setNavOpen(true)} className="p-1.5 rounded-md hover:bg-slate-100" data-testid="open-nav-sidebar">
          <Menu className="h-5 w-5 text-slate-600" />
        </button>
        <Cloud className="h-5 w-5 text-[#2B8FD8]" />
        <span className="text-sm font-bold text-slate-900">CloudFactory</span>
      </div>

      <main className="lg:ml-64 min-h-screen pt-12 lg:pt-0">
        {children}
      </main>
    </div>
  );
};

export default Layout;
